import { FormValidator } from '@app/calculators/effort-planner-form/form-validation/formValidator';
import { Validateable } from '@app/calculators/effort-planner-form/form-validation/validateable';
import { BehaviorSubject, Observable } from 'rxjs';

export interface AddressModelDTO {
  street: string;
  zipCode: string;
  place: string;
}

export interface AddressModelData {
  street: string | undefined;
  zipCode: string | undefined;
  place: string | undefined;
}

export type AddressModelValidationField = 'street' | 'zipCode' | 'place';

export class AddressModel implements Validateable {
  private streetSubject$: BehaviorSubject<string | undefined>;
  private zipCodeSubject$: BehaviorSubject<string | undefined>;
  private placeSubject$: BehaviorSubject<string | undefined>;

  readonly street$: Observable<string | undefined>;
  readonly zipCode$: Observable<string | undefined>;
  readonly place$: Observable<string | undefined>;

  readonly addressFormValidator = new FormValidator<AddressModelValidationField>();

  constructor(data: AddressModelData | undefined) {
    this.streetSubject$ = new BehaviorSubject(data?.street);
    this.zipCodeSubject$ = new BehaviorSubject(data?.zipCode);
    this.placeSubject$ = new BehaviorSubject(data?.place);

    this.street$ = this.streetSubject$.asObservable();
    this.zipCode$ = this.zipCodeSubject$.asObservable();
    this.place$ = this.placeSubject$.asObservable();
  }

  get street() {
    return this.streetSubject$.value;
  }

  set street(value: string | undefined) {
    this.streetSubject$.next(value);
  }

  get zipCode() {
    return this.zipCodeSubject$.value;
  }

  set zipCode(value: string | undefined) {
    this.zipCodeSubject$.next(value);
  }

  get place() {
    return this.placeSubject$.value;
  }

  set place(value: string | undefined) {
    this.placeSubject$.next(value);
  }

  resetValidations(): void {
    this.addressFormValidator.reset();
  }

  validate(): void {
    this.addressFormValidator.validate(
      'street',
      'settings.messages.validate.orgStreet.required',
      (this.street?.length ?? 0) > 0,
    );

    this.addressFormValidator.validate(
      'zipCode',
      'settings.messages.validate.orgZipcode.required',
      (this.zipCode?.length ?? 0) > 0,
    );

    this.addressFormValidator.validate(
      'zipCode',
      'settings.messages.validate.orgZipcode.numeric',
      this.zipCode ? /^\d+$/.test(this.zipCode) : false,
    );

    this.addressFormValidator.validate(
      'zipCode',
      'settings.messages.validate.orgZipcode.length',
      (this.zipCode?.length ?? 0) === 4,
    );

    this.addressFormValidator.validate(
      'place',
      'register.form.billingContact.orgPlaceBilling.required',
      (this.place?.length ?? 0) > 0,
    );
  }

  isValid(): boolean {
    return this.addressFormValidator.isValid;
  }
}
